import { formatDate } from '@angular/common';
import { Injectable, Injector } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GUID_EMPTY, TransactionType } from '@core';
import { ACLService } from '@delon/acl';
import { _HttpClient } from '@delon/theme';
import { NzMessageService } from 'ng-zorro-antd/message';
import { catchError, EMPTY, Subject, Observable, from, throwError } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { SalesDocTransferService } from 'src/app/shared-feature/_providers/sales-doc-transfer.service';
import { DocBaseService } from 'src/app/shared-feature/services/_base/doc-base.service';
import { AttachmentChangePayload } from 'src/app/shared/models/attachment-change.model';
import { StockQuantityControlService } from 'src/app/shared/services/stock-quantity-control.service';
import { ValidPostingPeriodCheckingService } from 'src/app/shared/services/valid-posting-period-checking.service';

@Injectable()
export class SoService extends DocBaseService {
  constructor(injector: Injector) {
    super(injector);
    this.aclKey = 'so';
    this.transactionType = TransactionType.SO;
  }

  qtyWasModify: boolean = false;
  customer: any;

  private newLineAddedSubject = new Subject<void>();
  newLineAdded$ = this.newLineAddedSubject.asObservable();

  private componentRef: any;
  setComponentRef(ref: any) {
    this.componentRef = ref;
  }

  get acl() {
    return this.injector.get(ACLService);
  }

  get transferSrv() {
    return this.injector.get(SalesDocTransferService);
  }

  get vppSrv() {
    return this.injector.get(ValidPostingPeriodCheckingService);
  }

  get sqcSrv() {
    return this.injector.get(StockQuantityControlService);
  }

  get router() {
    return this.injector.get(Router);
  }

  form = this.createForm();

  updatingRate = false;

  public get itemDetails() {
    return this.form.controls.itemDetails;
  }

  public get isTransferred() {
    if (this.itemDetails.value.find(o => o.transferFromQuotationDetailId))
      return true;
    return false;
  }

  clone() {
    let obj = { ...this._data };
    delete obj.id;
    obj.id = GUID_EMPTY;
    obj.docCode = '';
    obj.docDate = new Date();
    obj.taxDate = new Date();
    obj.isCancelled = false;
    obj.cancelledDate = null;
    obj.cancelledRemark = null;
    obj.transferStatus = 0;
    obj.transferFrom = null;
    obj.transferTo = null;
    obj.depositId = null;
    obj.depositGroupId = null;
    obj.depositInfo = null;
    obj.itemDetails.forEach((item: any) => {
      delete item.transferFromQuotationDetailId;
      item.transferFromQuotationDetail = null;
      item.transferredQty = 0;
    });
    return obj;
  }

  new() {
    const qtId = this.injector.get(ActivatedRoute).snapshot.queryParams['qt'];
    if (qtId) {
      return this.http.post('api/salestransfers/TransferQtToSo', {
        _silent: true,
        id: qtId
      });
    }
    return this.http.get('api/salesorders/new');
  }

  get(id: string): Observable<any> {
    return this.http.get(`api/salesorders/${id}`);
  }

  create(body: any) {
    return from(
      this.sqcSrv.check('SO', this.form.value.itemDetails, true)
    ).pipe(
      switchMap(() => this.http.post('api/salesorders/create/', body)),
      catchError(() => throwError(() => new Error('User cancelled')))
    );
    //return this.http.post('api/salesorders/create/', body);
  }

  update(body: any) {
    return from(
      this.sqcSrv.check('SO', this.form.value.itemDetails, this.qtyWasModify, this._data?.id)
    ).pipe(
      tap(() => {
        this.qtyWasModify = false;
      }),
      switchMap(() => this.http.post('api/salesorders/update/', body)),
      catchError(() => throwError(() => new Error('User cancelled')))
    );
    //return this.http.post('api/salesorders/update/', body);
  }

  override onAction(action: string, data: any): Promise<boolean> {
    const type = action?.toLowerCase();
    switch (action) {
      case 'save':
        return this.handleSave();
      default:
        return super.onAction(`${type}`, data);
    }
  }

  void(body: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post('api/salesorders/void', body).subscribe({
        next: (res: any) => {
          resolve(res);
        },
        error: () => {
          reject();
        }
      });
    });
  }

  devoid(id: string) {
    return this.http.post(`api/salesorders/devoid`, { id });
  }

  override close(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post('api/salesorders/close', { id }).subscribe({
        next: (res: any) => {
          resolve(res);
        },
        error: () => {
          reject();
        }
      });
    });
  }

  override reOpen(id: string) {
    return this.http.post(`api/salesorders/reopen`, { id });
  }
  setBounced() {
    return EMPTY;
  }

  clearChq() {
    return EMPTY;
  }

  setPDC() {}

  setAndSavePDC() {
    return EMPTY;
  }

  setAndSaveUnmarkPDC() {
    return EMPTY;
  }

  transferToSo() {}

  transferToDo(id: string) {
    this.reuseTab.close(`/sale/do/detail/0`, true);
    this.router.navigateByUrl(`/sale/do/detail/0?so=${id}`);
  }

  transferToSinv(id: string) {
    this.reuseTab.close(`/sale/inv/detail/0`, true);
    this.router.navigateByUrl(`/sale/inv/detail/0?so=${id}`);
  }

  transferToCs(id: string) {
    this.reuseTab.close(`/sale/cs/detail/0`, true);
    this.router.navigateByUrl(`/sale/cs/detail/0?so=${id}`);
  }

  transferToCsr() {}

  transferToPo() {}

  transferToGrn() {}

  transferToPinv() {}

  transferToCp() {}

  transferToCpf() {}

  doDocTransfer(docType: string) {
    const docIds = this.transferSrv.outstandingDocs
      .filter(x => x.isTransfer)
      .map(x => x.docId);

    // if (docIds.length === 0) {
    //   this.msg.error('No transaction was chosen');
    //   return EMPTY;
    // }

    return this.http.post(`api/SalesTransfers/TransferDocsToSo`, {
      docType: docType,
      fromDocIds: docIds,
      salesOrder: this.form.value
    });
  }

  delete(id: string) {
    return new Promise((resolve, reject) => {
      this.http
        .delete(`api/salesorders`, null, { body: { ids: [id] } })
        .subscribe({
          next: (res: any) => resolve(res),
          error: () => reject()
        });
    });
  }

  initForm(data: any) {
    this.itemDetails.clear();
    const isNew = data.id === GUID_EMPTY;
    const lineCount =
      (data.itemDetails?.length ?? 0 > 0)
        ? data.itemDetails.length
        : isNew
          ? 1
          : 0;
    this.addLines(lineCount);
  }

  createForm() {
    const form = this.fb.group({
      id: [<any>null, []],
      docCode: ['', [Validators.required, Validators.maxLength(50)]],
      docDate: [new Date(), [Validators.required]],
      taxDate: [new Date(), [Validators.required]],
      customerId: ['', [Validators.required]],
      customer: [<any>null, [Validators.required]],
      currencyId: ['', [Validators.required]],
      currency: [<any>null, [Validators.required]],
      currencyRate: [1, [Validators.required]],
      termId: ['', [Validators.required]],
      term: [<any>null, [Validators.required]],
      agentId: ['', []],
      agent: [<any>null, []],
      billingAddress: ['', [Validators.maxLength(400)]],
      referenceNo: ['', [Validators.maxLength(50)]],
      description: ['', [Validators.maxLength(250)]],
      validity: ['', [Validators.maxLength(50)]],
      attention: ['', [Validators.maxLength(200)]],
      deliveryTerm: ['', [Validators.maxLength(250)]],
      doBranchCode: ['', [Validators.maxLength(20)]],
      doAddress: ['', [Validators.maxLength(400)]],
      doBranchName: ['', [Validators.maxLength(250)]],
      doContact: ['', [Validators.maxLength(200)]],
      doPhoneNo: ['', [Validators.maxLength(100)]],
      doFaxNo: ['', [Validators.maxLength(100)]],
      isTaxInclusive: [false, []],
      isDeferredTax: [false, []],
      notes: ['', [Validators.maxLength(500)]],
      ref1: ['', [Validators.maxLength(50)]],
      ref2: ['', [Validators.maxLength(50)]],
      ref3: ['', [Validators.maxLength(50)]],
      ref4: ['', [Validators.maxLength(50)]],
      ref5: ['', [Validators.maxLength(50)]],
      remark1: ['', [Validators.maxLength(250)]],
      remark2: ['', [Validators.maxLength(250)]],
      remark3: ['', [Validators.maxLength(250)]],
      remark4: ['', [Validators.maxLength(250)]],
      remark5: ['', [Validators.maxLength(250)]],
      projectId: [<any>null, []],
      project: [<any>null, []],
      stockLocationId: [<any>null, []],
      stockLocation: [<any>null, []],
      totalAmount: [0, []],
      totalServiceAmount: [0, []],
      discountTotalAmount: [0, []],
      subtotalAmount: [0, []],
      taxTotalAmount: [0, []],
      rentalLeaseExemptedTaxTotalAmount: [0, []],
      wTaxTotalAmount: [0, []],
      taxExclusiveTotalAmount: [0, []],
      netTotalAmount: [0, []],
      amountExemptedFromTax: [<number>0, []],
      taxExemptionTotalAmount: [<number>0, []],
      itemDetails: this.fb.array([this.createLine()]),
      //Einvoice field
      customerName: ['', [Validators.maxLength(250)]],
      customerPhone: ['', [Validators.maxLength(100)]],
      email: ['', [Validators.email, Validators.maxLength(300)]],
      customerTinType: [<any>null, []],
      tinNumber: ['', [Validators.maxLength(14)]],
      registrationNo: ['', [Validators.maxLength(40)]],
      salesTaxRegNo: ['', [Validators.maxLength(35)]],
      serviceTaxRegNo: ['', [Validators.maxLength(35)]],
      address1: ['', [Validators.maxLength(300)]],
      address2: ['', [Validators.maxLength(300)]],
      address3: ['', [Validators.maxLength(100)]],
      address4: ['', [Validators.maxLength(100)]],
      zipCode: ['', [Validators.maxLength(20)]],
      city: ['', [Validators.maxLength(50)]],
      state: ['', [Validators.maxLength(100)]],
      country: ['', [Validators.maxLength(100)]]
    });
    form.controls.agent.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.form.controls.agentId.setValue(val?.id);
    });
    form.controls.term.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.form.controls.termId.setValue(val?.id);
    });

    form.controls.docDate.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      form.controls.taxDate.setValue(val);
    });

    form.controls.customer.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      if (val && val.id > 0) {
        form.controls.customerId.setValue(val.id);
        form.controls.customerName.setValue(val.name);
        this.http
          .get(`api/customers/${val.id}/defaultvalues`)
          .subscribe((res: any) => {
            if (res) {
              form.patchValue({
                agent: res.agent,
                agentId: res.agent?.id,
                term: res.term,
                termId: res.term?.id,
                currency: res.currency,
                currencyId: res.currency.id,
                currencyRate: res.currency.defaultSalesRate,
                attention: res.contactPerson,
                billingAddress: this.formatAddress(res),
                doAddress: this.formatDoAddress(res),
                doBranchCode: res.branchCode,
                doBranchName: res.branchName,
                doContact: res.doContactPerson,
                doPhoneNo: res.doPhoneNo,
                doFaxNo: res.faxNo1,
                customerName: res.name,
                customerPhone: res.phoneNo1,
                email: res.email,
                customerTinType: res.tinType,
                tinNumber: res.tinNumber,
                registrationNo: res.registrationNo,
                salesTaxRegNo: res.salesTaxRegNo,
                serviceTaxRegNo: res.serviceTaxRegNo,
                address1: res.address1,
                address2: res.address2,
                address3: res.address3,
                address4: res.address4,
                zipCode: res.zipCode,
                city: res.city,
                state: res.state,
                country: res.country
              });
            }

            if (
              this.acl.can('m1.8388608') &&
              this.country === 'MY' &&
              this.form.controls.itemDetails.value
            ) {
              this.form.controls.itemDetails.controls.forEach(line => {
                if (
                  line.controls.stockId.value &&
                  line.controls.stockId.value > 0
                ) {
                  this.http
                    .post(
                      'api/CustomerTaxExemptionItems/TaxExemptionDefaultValues?_silent=true',
                      {
                        customerId: val.id,
                        stockId: line.controls.stockId.value
                      }
                    )
                    .subscribe((taxExp: any) => {
                      if (taxExp) {
                        line.controls.taxCodeId.setValue(taxExp.taxCodeId);
                        line.controls.taxCode.setValue(taxExp.taxCode);
                        line.controls.taxExemptionNo.setValue(
                          taxExp.taxExemptionNo
                        );
                        line.controls.taxExemptionDetails.setValue(
                          taxExp.taxExemptionDetails
                        );
                        line.controls.taxExemptionRate.setValue(taxExp.taxCode?.exemptionRate);
                        line.controls.taxExemptionAmount.setValue(
                          this.calc.calcTaxExemptionAmount({ ...line.value, taxExemptionRate: taxExp.taxCode?.exemptionRate })
                        );
                      }
                    });
                }
              });
            }
          });
      } else {
        form.controls.customerId.setValue(null);
        form.controls.customerName.setValue(null);
      }
    });

    form.controls.currency.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.form.controls.currencyId.setValue(val?.id);

      this.observeChanges = false;
      if (val.local) {
        this.form.controls.currencyRate.setValue(1);
      } else {
        this.form.controls.currencyRate.setValue(val?.defaultSalesRate);
      }
      for (let line of this.itemDetails.controls) {
        this.recalcLine(line);
      }
      this.recalcHeader(form, this.itemDetails.value);
      this.observeChanges = true;
    });

    form.controls.currencyRate.valueChanges.subscribe((_: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      for (let line of this.itemDetails.controls) {
        this.recalcLine(line);
      }
      this.recalcHeader(form, this.itemDetails.value);
      this.observeChanges = true;
    });

    form.controls.isTaxInclusive.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;

      this.observeChanges = false;
      for (let line of this.itemDetails.controls) {
        line.controls.isTaxInclusive.setValue(val);
        this.recalcLine(line);
      }
      this.recalcHeader(form, this.itemDetails.value);
      this.observeChanges = true;
    });

    form.controls.project.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      form.controls.projectId.setValue(val?.id);

      this.observeChanges = false;
      for (let line of this.itemDetails.controls) {
        if (line.controls.stockId.value) {
          line.controls.project.setValue(val);
          line.controls.projectId.setValue(val?.id);
        }
      }
      this.observeChanges = true;
    });

    form.controls.stockLocation.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      form.controls.stockLocationId.setValue(val?.id);

      this.observeChanges = false;
      if (val) {
        for (let line of this.itemDetails.controls) {
          if (line.controls.stockId.value) {
            line.controls.stockLocation.setValue(val);
            line.controls.stockLocationId.setValue(val?.id);
          }
        }
      }
      this.observeChanges = true;
    });

    form.valueChanges.subscribe(() => {
      if (!this.observeChanges) return;
      this.modified = true;
    });
    this.userDefaultService.setControlRequired(form.get('agent')!, 'Agent');
    this.userDefaultService.setControlRequired(form.get('stockLocation')!, 'Location');
    this.userDefaultService.setControlRequired(form.get('project')!, 'Project');
    return form;
  }

  lineValueChanged = new Subject<{ line: FormGroup; controlName: string }>();

  createLine() {
    const line = this.fb.group({
      id: [GUID_EMPTY, []],  // ✅ Use GUID_EMPTY for new items (not null)
      pos: [0, []],
      numbering: [<any>null, []],
      isBundled: [false, []],
      isSubItem: [false, []],
      bundleId: [<any>null, []],
      stockId: [<any>null, []],
      stock: [<any>null, []],
      uomId: [<any>null, []],
      uom: [<any>null, []],
      accountId: [<any>null, []],
      account: [<any>null, []],
      description: ['', [Validators.maxLength(250)]],
      docFurtherDescription: ['', [Validators.maxLength(999999)]],
      referenceNo: ['', [Validators.maxLength(50)]],
      unitPrice: [0, []],
      qty: [0, []],
      amount: [0, []],
      ref1: ['', [Validators.maxLength(200)]],
      ref2: ['', [Validators.maxLength(200)]],
      dateRef1: [<any>null, []],
      dateRef2: [<any>null, []],
      taxCodeId: [<any>null, []],
      taxCode: [<any>null, []],
      taxRate: [0, []],
      rentalLeaseExemptedRate: [0, []],
      wTaxCodeId: [<any>null, []],
      wTaxCode: [<any>null, []],
      wTaxRate: [0, []],
      projectId: [<any>null, []],
      project: [<any>null, []],
      stockLocationId: [<any>null, []],
      stockLocation: [<any>null, []],
      tariffCodeId: [<any>null, []],
      tariffCode: [<any>null, []],
      isTaxInclusive: [false, []],
      subAmount: [0, []],
      discount: ['', [Validators.maxLength(50)]],
      discountAmount: [0, []],
      taxAmount: [0, []],
      rentalLeaseExemptedTaxAmount: [0, []],
      taxAmountAdjustment: [0, []],
      wTaxAmount: [0, []],
      wTaxAmountAdjustment: [0, []],
      taxExclusiveAmount: [0, []],
      netAmount: [0, []],
      serviceAmount: [0, []],
      amountExemptedTax: [0, []], // Amount Exempted from Tax
      taxExemptionRate: [0, []],
      taxExemptionAmount: [0, []], // Exempted Tax Amount
      taxExemptionNo: ['', [Validators.maxLength(20)]],
      taxExemptionDetails: ['', [Validators.maxLength(300)]],
      transferFromQuotationDetailId: [<any>null, []],
      transferFromQuotationDetail: [<any>null, []]
    });

    // Select stock
    line.controls.stock.valueChanges.subscribe((stock: any) => {
      if (this.disableChanges['stock']) return;
      if (!this.observeChanges) return;
      this.bundleHelper.prepareLineForStockChange(this.itemDetails, line);

      if (stock) {
        const headerIndex = this.itemDetails.controls.indexOf(line);
        if (this.bundleHelper.isBundleStock(stock)) {
          const master = this.form.value;
          const transactionDate = master.docDate
            ? formatDate(master.docDate, 'yyyy-MM-dd', 'en-US')
            : null;
          this.observeChanges = false;
          line.patchValue({
            stockId: stock.id,
            description: stock.name,
            qty: line.controls.qty.value || 1,
            isBundled: true,
            isSubItem: false,
            bundleId: null
          });
          this.bundleHelper
            .expandBundleSelection({
              formArray: this.itemDetails,
              headerLine: line,
              headerIndex,
              stock,
              purchase: false,
              currencyRate: master.currencyRate,
              customerId: master.customerId,
              transactionDate,
              createLine: () => this.createLine(),
              recalcLine: l => this.recalcLine(l)
            })
            .then(() => {
              this.bundleHelper.updateBundleHeaderFromSubItems(
                this.itemDetails,
                line
              );
              this.reorderNumbering();
              this.recalcHeader(this.form, this.itemDetails.value);
              this.observeChanges = true;
            });
          this.lineValueChanged.next({ line, controlName: 'stock' });
          return;
        }

        const master = this.form.value;
        let defaultValues = {
          stockId: stock.id,
          description: stock.name,
          qty: 1,
          project: master.project,
          projectId: master.projectId,
          stockLocation: master.stockLocation ?? stock.location,
          stockLocationId: master.stockLocationId ?? stock.location?.id,
          isTaxInclusive: master.isTaxInclusive
        };
        // assign default values
        this.http
          .get('api/stocks/DefaultValues', {
            stockId: stock.id,
            currencyRate: master.currencyRate,
            purchase: false,
            customerId: master.customerId,
            qty: line.controls.qty.value || 1,
            transactionDate: master.docDate ? formatDate(master.docDate, 'yyyy-MM-dd', 'en-US') : null
          })
          .subscribe((res: any) => {
            this.observeChanges = false;
            if (res) {
              stock.unitPrice = res.unitPrice;

              Object.assign(defaultValues, {
                ...res,
                account: res.salesAccount,
                accountId: res.salesAccount?.id,
                tariffCode: res.tariffCode,
                tariffCodeId: res.tariffCode?.id,
                docFurtherDescription: res.furtherDescription,
                doPhoneNo: res.doPhoneNo,
                doFaxNo: res.doFaxNo,
                stockLocation: master.stockLocation ?? res.stockLocation,
                stockLocationId: master.stockLocationId ?? res.stockLocation?.id
              });

              if (this.acl.can('m1.512')) {
                Object.assign(defaultValues, {
                  taxCode: res.outputTaxCode,
                  taxCodeId: res.outputTaxCode?.id,
                  taxRate: res.outputTaxCode ? res.outputTaxCode.rate : 0,
                  taxExemptionRate: res.outputTaxCode ? res.outputTaxCode.exemptionRate : 0,
                  rentalLeaseExemptedRate: res.outputTaxCode?.rlServiceTaxExemptedRate ?? 0
                });
              }

              line.patchValue(defaultValues);
              this.recalcLine(line);
              this.recalcHeader(
                this.form,
                this.form.controls.itemDetails.value
              );
            }

            if (
              this.acl.can('m1.8388608') &&
              this.country === 'MY' &&
              this.form.controls.customerId.value &&
              this.form.controls.customerId.value !== '0'
            ) {
              this.http
                .post(
                  'api/CustomerTaxExemptionItems/TaxExemptionDefaultValues?_silent=true',
                  {
                    customerId: this.form.controls.customerId.value,
                    stockId: stock.id
                  }
                )
                .subscribe((taxExp: any) => {
                  if (taxExp) {
                    line.controls.taxCodeId.setValue(taxExp.taxCodeId);
                    line.controls.taxCode.setValue(taxExp.taxCode);
                    line.controls.taxExemptionNo.setValue(
                      taxExp.taxExemptionNo
                    );
                    line.controls.taxExemptionDetails.setValue(
                      taxExp.taxExemptionDetails
                    );
                    line.controls.taxExemptionRate.setValue(taxExp.taxCode?.exemptionRate);
                  }
                });
            }

            this.reorderNumbering();
            this.observeChanges = true;
          });

        // implement existing row have value and auto next row
        // add new line if the next line doesnt exists
        var curPos = line.value.pos;
        if (curPos) {
          // If the current position is the last line, add a new line
          if (curPos >= this.itemDetails.value.length) this.addLines(1);
        }
      } else {
        this.observeChanges = false;
        line.patchValue({
          numbering: null,
          description: '',
          docFurtherDescription: '',
          referenceNo: '',
          taxCode: null,
          taxCodeId: null,
          tariffCode: null,
          tariffCodeId: null,
          taxRate: 0,
          amount: 0,
          ref1: '',
          ref2: '',
          dateRef1: null,
          dateRef2: null,
          wTaxCode: null,
          wTaxCodeId: null,
          wTaxRate: 0,
          project: null,
          projectId: null,
          stockLocation: null,
          stockLocationId: null,
          account: null,
          accountId: null,
          stock: null,
          stockId: null,
          uomId: null,
          uom: null,
          qty: 0,
          unitPrice: 0,
          subAmount: 0,
          taxAmount: 0,
          taxExclusiveAmount: 0,
          netAmount: 0,
          wTaxAmount: 0,
          amountExemptedTax: 0,
          taxExemptionRate: 0,
          taxExemptionAmount: 0,
          taxExemptionNo: '',
          taxExemptionDetails: ''
        });
        this.reorderNumbering();
        this.recalcLine(line);
        this.recalcHeader(this.form, this.form.controls.itemDetails.value);
        this.observeChanges = true;
      }
      this.lineValueChanged.next({ line, controlName: 'stock' });
    });

    line.controls.qty.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.value.qty = val;
      this.qtyWasModify = true;

      const isBundleHeader =
        line.controls.isBundled?.value === true &&
        line.controls.isSubItem?.value !== true;
      if (isBundleHeader) {
        this.bundleHelper.syncBundleSubItemQty(this.itemDetails, line);
        this.itemDetails.controls.forEach(subLine => {
          const fg = subLine as FormGroup;
          if (fg.get('bundleId')?.value === line.get('id')?.value) {
            if (fg.get('isSubItem')?.value === true) {
              this.recalcLine(fg);
            }
          }
        });
        this.bundleHelper.updateBundleHeaderFromSubItems(this.itemDetails, line);
        this.recalcHeader(this.form, this.itemDetails.value);
        this.observeChanges = true;
        return;
      }

      // Recalculate price based on quantity (for price rules with MinQty)
      // Only if price rule feature is enabled
      if (this.acl.can('pricerule.enabled')) {
        const master = this.form.value;
        const stock = line.controls.stock.value;
        if (stock && master.customerId && val > 0) {
          this.http
            .get('api/stocks/DefaultValues', {
              stockId: stock.id,
              currencyRate: master.currencyRate,
              purchase: false,
              customerId: master.customerId,
              qty: val,
              transactionDate: master.docDate ? formatDate(master.docDate, 'yyyy-MM-dd', 'en-US') : null
            })
            .subscribe((res: any) => {
              if (res && res.unitPrice !== line.controls.unitPrice.value) {
                line.controls.unitPrice.setValue(res.unitPrice);
              }
            });
        }
      }

      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
    });

    line.controls.uom.valueChanges.subscribe((uom: any) => {
      if (!this.observeChanges) return;
      line.controls.uomId.setValue(uom?.id);
      this.observeChanges = false;
      const master = this.form.value;
      const stock = line.controls.stock.value;

      const finishUomChange = () => {
        this.recalcLine(line);
        this.recalcHeader(this.form, this.itemDetails.value);
        this.observeChanges = true;
        this.lineValueChanged.next({ line, controlName: 'uom' });
      };

      const isBundleHeader =
        line.controls.isBundled?.value === true &&
        line.controls.isSubItem?.value !== true;
      if (isBundleHeader) {
        if (uom && stock) {
          let listPrice: number =
            uom.salesPrice > 0
              ? <number>uom.salesPrice
              : (stock.unitPrice || 0) * uom.rate;
          if (!listPrice) {
            listPrice = 0;
          }
          let unitPrice = master.currencyRate
            ? listPrice / master.currencyRate
            : listPrice;
          if (!unitPrice) {
            unitPrice = 0;
          }
          line.controls.unitPrice.setValue(unitPrice);
        }
        finishUomChange();
        return;
      }

      if (uom && stock) {
        this.http
          .get('api/stocks/DefaultValues', {
            stockId: stock.id,
            currencyRate: master.currencyRate,
            purchase: false,
            customerId: master.customerId,
            qty: line.controls.qty.value || 1,
            transactionDate: master.docDate
              ? formatDate(master.docDate, 'yyyy-MM-dd', 'en-US')
              : null,
            uomId: uom.id
          })
          .subscribe({
            next: (res: any) => {
              if (res && res.unitPrice != null) {
                line.controls.unitPrice.setValue(res.unitPrice);
              }
              finishUomChange();
            },
            error: () => {
              let listPrice: number =
                uom.salesPrice > 0
                  ? <number>uom.salesPrice
                  : (stock.unitPrice || 0) * uom.rate;
              if (!listPrice) {
                listPrice = 0;
              }
              let unitPrice = master.currencyRate
                ? listPrice / master.currencyRate
                : listPrice;
              if (!unitPrice) {
                unitPrice = 0;
              }
              line.controls.unitPrice.setValue(unitPrice);
              finishUomChange();
            }
          });
        return;
      }

      finishUomChange();
    });

    line.controls.unitPrice.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.value.unitPrice = val;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
    });

    line.controls.discount.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.value.discount = val;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      line.patchValue({ discountAmount: line.value.discountAmount });
      this.observeChanges = true;
    });

    line.controls.taxCode.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.taxCodeId.setValue(val?.id);
      line.controls.taxRate.setValue(val?.rate ?? 0);
      line.controls.taxExemptionRate.setValue(val?.exemptionRate ?? 0);
      line.controls.rentalLeaseExemptedRate.setValue(val?.rlServiceTaxExemptedRate ?? 0);
      if (!val) {
        line.controls.taxAmountAdjustment.setValue(0);
      }
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
      this.lineValueChanged.next({ line, controlName: 'taxCode' });
    });
    
    line.controls.taxExemptionRate.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.value.taxExemptionRate = val;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
      this.lineValueChanged.next({ line, controlName: 'taxExemptionRate' });
    });

    line.controls.tariffCode.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.tariffCodeId.setValue(val?.id);
      this.observeChanges = true;

      if (this.country == 'MY') this.newLineAddedSubject.next();
      this.lineValueChanged.next({ line, controlName: 'tariffCode' });
    });

    line.controls.wTaxCode.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.wTaxCodeId.setValue(val?.id);
      line.controls.wTaxRate.setValue(val?.rate ?? 0);
      if (!val) {
        line.controls.wTaxAmountAdjustment.setValue(0);
      }
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
      this.lineValueChanged.next({ line, controlName: 'wTaxCode' });
    });
    line.controls.project.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      line.controls.projectId.setValue(val?.id);
      this.lineValueChanged.next({ line, controlName: 'project' });
    });
    line.controls.stockLocation.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      line.controls.stockLocationId.setValue(val?.id);
      this.lineValueChanged.next({ line, controlName: 'stockLocation' });
    });

    line.controls.amount.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.value.amount = val;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
    });

    line.controls.taxAmountAdjustment.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
    });

    line.controls.wTaxAmountAdjustment.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
    });

    return line;
  }

  reorderNumbering() {
    let numbering = 1;
    for (let i = 0; i < this.itemDetails.length; i++) {
      const item = this.itemDetails.at(i);
      if (item.value.isSubItem === true) {
        item.patchValue({ numbering: null });
        continue;
      }
      if (item.value.stockId) {
        item.patchValue({ numbering: numbering });
        numbering++;
      }
    }
  }

  updateLatestRate() {
    this.updatingRate = true;
    this.http
      .post('api/currencies/latestrate?_silent=true', {
        currencyCode: this.form.controls.currency.value.code,
        isAR: true
      })
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.form.controls.currencyRate.setValue(res);
          }
          this.updatingRate = false;
        },
        error: () => (this.updatingRate = false)
      });
  }

  updateCustInfo(data: any) {
    this.form.controls.tinNumber.setValue(data.tinNumber);
    this.form.controls.registrationNo.setValue(data.registrationNo);
    this.form.controls.customerPhone.setValue(data.customerPhone);
    this.form.controls.email.setValue(data.email);
    this.form.controls.salesTaxRegNo.setValue(data.salesTaxRegNo);
    this.form.controls.serviceTaxRegNo.setValue(data.serviceTaxRegNo);
    this.form.controls.customerTinType.setValue(data.customerTinType);
    this.form.controls.zipCode.setValue(data.zipCode);
    this.form.controls.city.setValue(data.city);
    this.form.controls.state.setValue(data.state);
    this.form.controls.country.setValue(data.country);
    this.form.controls.address1.setValue(data.address1);
    this.form.controls.address2.setValue(data.address2);
    this.form.controls.address3.setValue(data.address3);
    this.form.controls.address4.setValue(data.address4);
  }

  addLines(count: number = 1) {
    const isTaxIncl = this.form.controls.isTaxInclusive.value;
    for (let i = 0; i < count; i++) {
      let line = this.createLine();
      this.observeChanges = false;
      line.patchValue({
        pos: this.itemDetails.length + 1,
        isTaxInclusive: isTaxIncl
      });
      this.itemDetails.push(line);
      this.observeChanges = true;
    }
  }

  recalcLine(line: FormGroup) {
    line.controls['amount'].setValue(this.calc.calcAmount(line.value));
    line.controls['discountAmount'].setValue(
      this.calc.calcDiscount(line.value)
    );
    line.controls['subAmount'].setValue(this.calc.calcSubAmount(line.value));
    line.controls['taxAmount'].setValue(this.calc.calcTaxAmount(line.value));
    line.controls['rentalLeaseExemptedTaxAmount'].setValue(this.calc.calcRentalLeaseExemptedTaxAmount(line.value));
    line.controls['wTaxAmount'].setValue(this.calc.calcWTaxAmount(line.value));
    line.controls['taxExclusiveAmount'].setValue(
      this.calc.calcTaxExclusiveAmount(line.value)
    );
    line.controls['serviceAmount'].setValue(
      this.calc.calcServiceAmount(line.value)
    );
    line.controls['netAmount'].setValue(this.calc.calcNetAmount(line.value));
    line.controls['amountExemptedTax'].setValue(
      this.calc.calcAmountExemptedTax(line.value)
    );
    line.controls['taxExemptionAmount'].setValue(
      this.calc.calcTaxExemptionAmount(line.value)
    );
    this.bundleHelper.updateBundleHeaderFromLine(this.itemDetails, line);
  }

  recalcHeader(header: FormGroup, lines: any[]) {
    this.form.controls.totalAmount.setValue(
      this.calc.calcInventoryTotalAmount(lines)
    );
    this.form.controls.discountTotalAmount.setValue(
      this.calc.calcDiscountTotalAmount(lines)
    );
    this.form.controls.subtotalAmount.setValue(
      this.calc.calcSubtotalAmount(lines)
    );
    this.form.controls.taxTotalAmount.setValue(
      this.calc.calcTaxTotalAmount(lines)
    );
    this.form.controls.rentalLeaseExemptedTaxTotalAmount.setValue(
      this.calc.calcRentalLeaseExemptedTaxTotalAmount(lines)
    );
    this.form.controls.wTaxTotalAmount.setValue(
      this.calc.calcWTaxTotalAmount(lines)
    );
    this.form.controls.taxExclusiveTotalAmount.setValue(
      this.calc.calcTaxExclusiveTotalAmount(lines)
    );
    this.form.controls.totalServiceAmount.setValue(
      this.calc.calcTotalServiceAmount(lines)
    );
    this.form.controls.netTotalAmount.setValue(
      this.calc.calcNetTotalAmount(lines)
    );
    this.form.controls.amountExemptedFromTax.setValue(
      this.calc.calcTotalAmountExemptedTax(lines)
    );
    this.form.controls.taxExemptionTotalAmount.setValue(
      this.calc.calcTotalTaxExemptionAmount(lines)
    );
  }

  populateDOAddress(data: any) {
    // this.form.controls.doBranchCode.reset();
    // this.form.controls.doBranchName.reset();
    // this.form.controls.doAddress.reset();
    // this.form.controls.doContact.reset();
    // this.form.controls.doPhoneNo.reset();
    // this.form.controls.doFaxNo.reset();

    // if data have been selected
    if (data) {
      this.form.controls.doBranchCode.setValue(data.branchCode);
      this.form.controls.doBranchName.setValue(data.branchName);
      this.form.controls.doAddress.setValue(this.formatAddress(data));
      this.form.controls.doContact.setValue(data.contactPerson);
      this.form.controls.doPhoneNo.setValue(data.phoneNo);
      this.form.controls.doFaxNo.setValue(data.faxNo);
    } else {
      // if data have not been selected,so get default value from customer api
      this.http
        .get(
          `api/customers/${this.form.controls.customer.value.id}/defaultvalues`
        )
        .subscribe((res: any) => {
          this.customer = res;
          this.form.patchValue({
            doBranchCode: res.branchCode,
            doBranchName: res.branchName,
            doAddress: this.formatDoAddress(res),
            doContact: res.doContactPerson,
            doPhoneNo: res.doPhoneNo,
            doFaxNo: res.faxNo1
          });
        });
    }
  }

  private formatDoAddress(res: any): string {
    let addrArr = [
      res.doAddress1,
      res.doAddress2,
      res.doAddress3,
      res.doAddress4,
      res.doZipCode
    ];
    addrArr = addrArr.filter(function (val) {
      return val != null && val !== '';
    });
    let addr = addrArr.join('\r\n');
    return addr;
  }

  private handleSave(): Promise<boolean> {
    // Get deposit info
    if (this.componentRef) {
      const depositData = this.componentRef.getDepositRecords();
      if (depositData) {
        var processedData = depositData.map((item: any) => {
          if (item.id) return item;
          return {
            ...item,
            customerId: this.form.value.customerId,
            customer: this.form.value.customer,
            customerName: this.form.value.customerName,
            agentId: this.form.value.agentId,
            agent: this.form.value.agent
          };
        });

        this._data.depositInfo = {
          ...this._data.depositInfo,
          depositRecords: processedData
        };
      }
    }

    // Normalize itemDetails IDs to ensure they are GUID strings
    const itemDetailsArray = this.form.controls.itemDetails;
    if (itemDetailsArray?.controls && Array.isArray(itemDetailsArray.controls)) {
      itemDetailsArray.controls.forEach((line: any) => {
        const idControl = line.get?.('id');
        if (idControl && !this.isGuidString(idControl.value)) {
          idControl.setValue(GUID_EMPTY);
        }
      });
    }

    return this.vppSrv.check(this.form.value.docDate);
  }

  private isGuidString(value: any): boolean {
    if (!value || typeof value !== 'string') {
      return false;
    }
    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
      value
    );
  }

  override getAttachmentChangesPayload(): AttachmentChangePayload | null {
    if (this.componentRef && this.componentRef.collectAttachmentChanges) {
      return this.componentRef.collectAttachmentChanges();
    }
    return null;
  }

  override clearAttachmentChanges(): void {
    if (this.componentRef && this.componentRef.clearAttachmentChanges) {
      this.componentRef.clearAttachmentChanges();
    }
  }

  recalcAll() {
    this.form.controls.itemDetails.controls.forEach((line: any) => {
      this.recalcLine(line);
    });

    this.recalcHeader(this.form, this.form.controls.itemDetails.value);
  }
}
